import React, { useContext, useCallback, useEffect } from 'react';
import { PopperDropdownContent } from '../../../PopperDropdownLayout';
import StandardMenu from '../../../MenuPreset/StandardMenu';
import { NullableDropdownItem } from '../../../types';
import { DropdownStateContext } from '../../../DropdownStateManager';
import type { OffsetModifier, PopperPlacement } from '../../../../popper/types';

const defaultOffsetModifier: OffsetModifier = [0, 6];

export interface Props {
  id: string;
  items: NullableDropdownItem[];
  /**
   * Defines the accessible role of the StandardDropdownMenu.
   *
   * ---
   *
   * Use `listbox` in instances that are for traditional select boxes.
   * (e.g. a StandardDropdownMenu that contains a list of months, where the user must pick one)
   *
   * ---
   *
   * Use `menu` in instances where you need to display a list of buttons/links that aren't "selected" so much as "activated".
   * (e.g. a StandardDropdownMenu that contains a "Share" button and a "Report" button)
   */
  role: 'menu' | 'listbox';
  usePortal?: boolean;
  menuLabel?: React.ReactNode;
  placement?: PopperPlacement;
  offsetModifier?: OffsetModifier;
  initiallyOpen?: boolean;
  menuInnerRef?: React.RefCallback<HTMLElement>;
  className?: string;
  menuClassName?: string;
  popperClassName?: string;
  itemClassName?: string;
  menuScrollable?: boolean;
  hideCheckIcon?: boolean;
  menuLabelClassName?: string;
  withHoverEffect?: boolean;
  withScrollableArrows?: boolean;
  withMenuPaddings?: boolean; // menu paddings
  /**
   * @default true
   */
  allowFlip?: boolean;
  onSelect?: (value: any, label?: any) => void;
}

const StandardDropdownMenu: React.FC<Props> = ({
  id,
  items,
  role,
  usePortal,
  placement = 'bottom-start',
  initiallyOpen,
  className,
  menuClassName,
  popperClassName,
  itemClassName,
  offsetModifier = defaultOffsetModifier,
  menuInnerRef,
  menuLabel,
  menuScrollable,
  hideCheckIcon,
  menuLabelClassName,
  withHoverEffect,
  withMenuPaddings,
  withScrollableArrows,
  allowFlip = true,
  onSelect,
}) => {
  const { openDropdown, closeDropdown } = useContext(DropdownStateContext);

  const onMenuSelect = useCallback(
    (value: string | number | undefined, label: any) => {
      onSelect && onSelect(value, label);
      closeDropdown();
    },
    [onSelect, closeDropdown]
  );

  useEffect(() => {
    initiallyOpen && openDropdown();
    // adding openDropdown to this will cause it to stay open since
    // a change in isOpen will regenerate openDropdown and this will fire again
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiallyOpen]);

  return (
    <PopperDropdownContent
      usePortal={usePortal}
      placement={placement}
      offsetModifier={offsetModifier}
      popperClassName={popperClassName}
      allowFlip={allowFlip}
    >
      <StandardMenu
        id={id}
        className={className}
        menuClassName={menuClassName}
        scrollable={menuScrollable}
        label={menuLabel}
        labelClassName={menuLabelClassName}
        items={items}
        itemClassName={itemClassName}
        hideCheckIcon={hideCheckIcon}
        withHoverEffect={withHoverEffect}
        withScrollableArrows={withScrollableArrows}
        withPaddings={withMenuPaddings}
        onSelect={onMenuSelect}
        role={role}
        innerRef={menuInnerRef}
      />
    </PopperDropdownContent>
  );
};
StandardDropdownMenu.displayName = 'StandardDropdownMenu';

export default StandardDropdownMenu;
